import DOMPurify from "dompurify";

/** http(s)/mailto/tel only — blocks javascript:, data:, and other executable/unsafe schemes. */
const SAFE_URI_REGEXP = /^(?:https?|mailto|tel):/i;

/**
 * Same as SAFE_URI_REGEXP but also allows relative/internal links (leading
 * "/" or "#"), matching isSafeUrl below. Comment and document HTML contains
 * app-internal links (e.g. "/ticket/ticket-detail/123") — without this,
 * DOMPurify's ALLOWED_URI_REGEXP check would strip their href since it only
 * tests for absolute http(s)/mailto/tel URIs.
 */
const RICH_TEXT_SAFE_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[/#])/i;

const RICH_TEXT_ALLOWED_TAGS = [
  "p",
  "br",
  "hr",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "strike",
  "del",
  "a",
  "img",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "blockquote",
  "pre",
  "code",
  "span",
  "div",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
];

/**
 * `size`/`float` are emitted by this app's TipTap image node
 * (tiptap-editor/image-formatting.ts) and drive the `.customiframe img[size=…]`
 * rules in global.css; `type`/`istechniciangroup`/`data-hashtag`/`data-label`
 * come from the hashtag node. They are inert presentational attributes, but
 * dropping them would visibly change how existing comments render.
 */
const RICH_TEXT_ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "class",
  "title",
  "id",
  "data-id",
  "data-link-type",
  "data-hashtag",
  "data-label",
  "colspan",
  "rowspan",
  "width",
  "height",
  "size",
  "float",
  "type",
  "istechniciangroup",
];

/**
 * Tiny profile for form labels/headings: inline formatting, lists and headings
 * only (see the `.forms-rich-text` rules in global.css). No images, tables or
 * embedded markup — form builder content is technician-authored but is rendered
 * to unauthenticated contacts.
 */
const INLINE_TEXT_ALLOWED_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "br",
  "a",
  "span",
  "p",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
];

const INLINE_TEXT_ALLOWED_ATTR = ["href", "target", "rel", "class"];

/**
 * Belt-and-braces: these can never be reached through the allow-lists above,
 * but keep them explicitly forbidden so a later addition to ALLOWED_TAGS cannot
 * quietly re-open a script/style/form or a foreign-content (svg/math) mXSS path.
 */
const FORBIDDEN_TAGS = [
  "style",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "iframe",
  "svg",
  "math",
  "script",
];

/**
 * `target="_blank"` without `rel="noopener noreferrer"` hands the opened page a
 * `window.opener` handle back to this tab. Registered once, at module load, so
 * every sanitize() below inherits it.
 *
 * The guard is only for a DOM-less environment (DOMPurify exposes just a
 * factory there, with no addHook); in the browser the hook is always added.
 */
if (typeof DOMPurify.addHook === "function") {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

/**
 * Sanitizes HTML that may originate from an untrusted or third-party source
 * (inbound email, technician-authored comments and documents, Public API)
 * before it is bound via v-html. Strips <script>/event-handler vectors and
 * non-http(s)/mailto/tel URL schemes while preserving the formatting (bold,
 * links, images, tables, mention spans) the comment/document views rely on.
 */
/**
 * DOMPurify validates the VALUE of every allowed attribute that is not in its built-in URI-safe
 * set against ALLOWED_URI_REGEXP. Our regexp only admits http(s)/mailto/tel/relative URLs, so
 * plain-word values such as target="_blank", rel="noopener" or type="mention" would be stripped.
 * Only href and src carry URLs; everything else in the allow-list is exempted from that check.
 */
const RICH_TEXT_NON_URI_ATTR = RICH_TEXT_ALLOWED_ATTR.filter((attr) => attr !== "href" && attr !== "src");
const INLINE_TEXT_NON_URI_ATTR = INLINE_TEXT_ALLOWED_ATTR.filter((attr) => attr !== "href");

export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) {
    return "";
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
    ADD_URI_SAFE_ATTR: RICH_TEXT_NON_URI_ATTR,
    ALLOWED_URI_REGEXP: RICH_TEXT_SAFE_URI_REGEXP,
    FORBID_TAGS: FORBIDDEN_TAGS,
  });
}

/**
 * Stricter profile for form labels, headings and static text, which have no
 * legitimate need for images, tables or block embeds.
 */
export function sanitizeInlineText(html: string | null | undefined): string {
  if (!html) {
    return "";
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: INLINE_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: INLINE_TEXT_ALLOWED_ATTR,
    ADD_URI_SAFE_ATTR: INLINE_TEXT_NON_URI_ATTR,
    ALLOWED_URI_REGEXP: RICH_TEXT_SAFE_URI_REGEXP,
    FORBID_TAGS: FORBIDDEN_TAGS,
  });
}

/**
 * HTML-escapes a plain-text value so it can be interpolated into an HTML
 * string that is later bound via v-html / innerHTML. Use this for every
 * user-, technician- or integration-controlled field that is inserted into
 * template-literal markup. Escaping `&`, `<`, `>`, `"` and `'` makes the value
 * safe in both text and double/single-quoted attribute contexts.
 * null/undefined become "".
 */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Browsers drop ASCII tab/newline anywhere in a URL, and C0 controls/spaces at
 * its ends, before reading the scheme, so "java\tscript:alert(1)" still runs
 * as javascript:. Apply the same normalisation before any scheme check.
 */
export function stripUrlControlChars(href: string): string {
  return href
    .replace(/[\t\n\r]/g, "")
    .replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, "");
}

/**
 * True if href carries a scheme other than http(s)/mailto/tel (javascript:,
 * data:, vbscript:, …). Scheme-less values ("www.x.com", "/path", "#id") are
 * not unsafe — unlike isSafeUrl, this does not reject bare/relative links.
 */
export function hasUnsafeUrlScheme(href: string | null | undefined): boolean {
  const value = stripUrlControlChars(String(href ?? ""));
  return /^[a-z][a-z0-9+.-]*:/i.test(value) && !SAFE_URI_REGEXP.test(value);
}

/**
 * True if href uses an allowed scheme (http, https, mailto, tel) or is a
 * relative/internal path/hash. Used to guard window.open()/location.href
 * navigation and link rendering against javascript:/data: URI injection.
 */
export function isSafeUrl(href: string | null | undefined): boolean {
  if (!href) {
    return false;
  }
  const trimmed = stripUrlControlChars(href);
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) {
    return true;
  }
  return SAFE_URI_REGEXP.test(trimmed);
}
